import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Languages, ChevronDown } from 'lucide-react';

interface TextTranslationProps {
  className?: string;
  showLabel?: boolean;
}

// Supported languages - only English, French, and Spanish
const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
];

const STORAGE_KEY = 'google-translate-language';

// Global script loading state
let googleTranslateScriptLoaded = false;

/**
 * Clear ALL googtrans cookies across all possible domain variations
 */
const clearAllGoogtransCookies = (): void => {
  try {
    const domain = window.location.hostname;
    const isLocalhost = domain === 'localhost' || 
                       domain.includes('127.0.0.1') || 
                       domain === '' ||
                       domain.startsWith('192.168.');
    
    const expireDate = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const expireMaxAge = 'max-age=0';
    
    if (isLocalhost) {
      // For localhost, clear without domain
      document.cookie = `googtrans=; path=/; expires=${expireDate}`;
      document.cookie = `googtrans=; path=/; ${expireMaxAge}`;
    } else {
      // Clear all possible domain variations
      const domainsToClear = [
        domain,                    // www.tinywins.ai
        `.${domain}`,              // .www.tinywins.ai
        domain.replace(/^www\./, ''), // tinywins.ai (without www)
        `.${domain.replace(/^www\./, '')}`, // .tinywins.ai
      ];
      
      // Also clear without domain
      document.cookie = `googtrans=; path=/; expires=${expireDate}`;
      document.cookie = `googtrans=; path=/; ${expireMaxAge}`;
      
      // Clear for each domain variation
      domainsToClear.forEach(d => {
        try {
          document.cookie = `googtrans=; path=/; expires=${expireDate}; domain=${d}`;
          document.cookie = `googtrans=; path=/; ${expireMaxAge}; domain=${d}`;
        } catch (e) {
          // Some domain formats might not be valid, ignore
        }
      });
    }
  } catch (error) {
    // Error clearing cookies
  }
};

/**
 * Set googtrans cookie from localStorage BEFORE Google Translate initializes
 * This ensures Google Translate detects the language on initialization
 * IMPORTANT: Clears all existing cookies first, then sets only ONE cookie
 */
const setCookieFromLocalStorage = (): void => {
  try {
    const storedLangCode = localStorage.getItem(STORAGE_KEY);
    const domain = window.location.hostname;
    const isLocalhost = domain === 'localhost' || 
                       domain.includes('127.0.0.1') || 
                       domain === '' ||
                       domain.startsWith('192.168.');
    
    // ALWAYS clear all existing cookies first to prevent duplicates
    clearAllGoogtransCookies();
    
    if (storedLangCode && storedLangCode !== 'en') {
      const cookieValue = `/en/${storedLangCode}`;
      
      // Set ONLY ONE cookie with the correct value
      // Use the base domain (without www) for better compatibility
      if (isLocalhost) {
        // For localhost, set without domain
        document.cookie = `googtrans=${cookieValue}; path=/; max-age=31536000`;
      } else {
        // For production, use the base domain (without www prefix)
        const baseDomain = domain.replace(/^www\./, '');
        // Set with .domain format (most compatible) - this will work for both www and non-www
        try {
          document.cookie = `googtrans=${cookieValue}; path=/; max-age=31536000; domain=.${baseDomain}`;
        } catch (e) {
          // Fallback: set without domain if .domain format fails
          document.cookie = `googtrans=${cookieValue}; path=/; max-age=31536000`;
        }
      }
    } else if (storedLangCode === 'en') {
      // For English, ensure all cookies are cleared (already done above)
      // No need to set a cookie for English
    }
  } catch (error) {
    // Error setting cookie
  }
};

/**
 * Apply translation directly via Google Translate select element
 * Uses localStorage as source of truth, no cookies needed
 */
const applyTranslationFromLocalStorage = (langCode: string, forceReload: boolean = false): boolean => {
  const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
  
  if (!selectElement || !selectElement.options || selectElement.options.length === 0) {
    return false;
  }

  const options = Array.from(selectElement.options);
  const cookieValue = `/en/${langCode}`;
  
  // Find the matching option - try multiple patterns
  // Google Translate uses different formats, so we need to check various patterns
  let option = options.find(opt => {
    const value = opt.value;
    // Try exact match first
    if (value === cookieValue) return true;
    // Try with different separators
    if (value === `/en/${langCode}`) return true;
    if (value === `en|${langCode}`) return true;
    if (value === `${langCode}`) return true;
    // Try contains patterns
    if (value.includes(`/${langCode}`) && !value.includes('/fr') && !value.includes('/es')) return true;
    if (value.includes(`|${langCode}|`)) return true;
    if (value.startsWith(`${langCode}|`)) return true;
    if (value.endsWith(`|${langCode}`)) return true;
    // Check option text as fallback
    const text = opt.text.toLowerCase();
    const langName = SUPPORTED_LANGUAGES.find(l => l.code === langCode)?.name.toLowerCase();
    if (langName && text.includes(langName)) return true;
    return false;
  });
  
  if (option) {
    const currentValue = selectElement.value;
    const newValue = option.value;
    const optionIndex = options.indexOf(option);
    
    if (currentValue !== newValue || forceReload) {
      // Method 1: Set value and trigger native change handler
      selectElement.selectedIndex = optionIndex;
      selectElement.value = newValue;
      
      // Method 2: Try to call the native onchange handler directly
      if (selectElement.onchange) {
        try {
          selectElement.onchange(new Event('change') as any);
        } catch (e) {
          // Ignore
        }
      }
      
      // Method 3: Focus and click to simulate user interaction
      selectElement.focus();
      
      // Method 4: Create a more realistic change event
      const changeEvent = new Event('change', { 
        bubbles: true, 
        cancelable: true 
      });
      Object.defineProperty(changeEvent, 'target', {
        value: selectElement,
        enumerable: true,
        configurable: true
      });
      Object.defineProperty(changeEvent, 'currentTarget', {
        value: selectElement,
        enumerable: true,
        configurable: true
      });
      
      // Dispatch the change event
      selectElement.dispatchEvent(changeEvent);
      
      // Method 5: Try input event
      const inputEvent = new Event('input', { 
        bubbles: true, 
        cancelable: true 
      });
      Object.defineProperty(inputEvent, 'target', {
        value: selectElement,
        enumerable: true,
        configurable: true
      });
      selectElement.dispatchEvent(inputEvent);
      
      // Method 6: Try mouse events to simulate real user interaction
      const mouseDownEvent = new MouseEvent('mousedown', { 
        bubbles: true, 
        cancelable: true,
        view: window,
        buttons: 1
      });
      selectElement.dispatchEvent(mouseDownEvent);
      
      const mouseUpEvent = new MouseEvent('mouseup', { 
        bubbles: true, 
        cancelable: true,
        view: window,
        buttons: 1
      });
      selectElement.dispatchEvent(mouseUpEvent);
      
      const clickEvent = new MouseEvent('click', { 
        bubbles: true, 
        cancelable: true,
        view: window,
        buttons: 1
      });
      selectElement.dispatchEvent(clickEvent);
      
      // Method 7: Try to access Google Translate's internal translation function
      try {
        // Check if Google Translate has an internal translate function
        const googleTranslate = (window as any).google?.translate;
        if (googleTranslate && googleTranslate.translate) {
          // Try to call translate directly
          googleTranslate.translate(langCode);
        }
      } catch (e) {
        // Ignore
      }
      
      // Method 8: Simulate full user interaction - open dropdown, select option, close
      // This mimics what a real user would do
      setTimeout(() => {
        // Verify the value is still set
        if (selectElement.value === newValue) {
          // Check if translation was applied by looking at body classes or translated content
          const isTranslated = document.body.classList.contains('translated-ltr') || 
                               document.body.classList.contains('translated-rtl') ||
                               document.body.getAttribute('dir') === 'rtl' ||
                               document.querySelector('.goog-te-banner-frame') !== null ||
                               document.querySelector('[class*="translated"]') !== null;
          
          // If not translated yet, try a more aggressive approach
          if (!isTranslated && langCode !== 'en') {
            // Try to simulate opening the select dropdown
            selectElement.focus();
            
            // Create a keydown event to open the dropdown (Space or Enter)
            const keyDownEvent = new KeyboardEvent('keydown', {
              key: ' ',
              code: 'Space',
              keyCode: 32,
              bubbles: true,
              cancelable: true
            });
            selectElement.dispatchEvent(keyDownEvent);
            
            // Wait a bit, then set the value and close
            setTimeout(() => {
              selectElement.value = newValue;
              selectElement.selectedIndex = optionIndex;
              
              // Create keyup event
              const keyUpEvent = new KeyboardEvent('keyup', {
                key: ' ',
                code: 'Space',
                keyCode: 32,
                bubbles: true,
                cancelable: true
              });
              selectElement.dispatchEvent(keyUpEvent);
              
              // Trigger change event
              const finalChangeEvent = new Event('change', { 
                bubbles: true, 
                cancelable: true 
              });
              Object.defineProperty(finalChangeEvent, 'target', {
                value: selectElement,
                enumerable: true,
                configurable: true
              });
              selectElement.dispatchEvent(finalChangeEvent);
              
              // Also try pressing Enter to confirm selection
              setTimeout(() => {
                const enterKeyEvent = new KeyboardEvent('keydown', {
                  key: 'Enter',
                  code: 'Enter',
                  keyCode: 13,
                  bubbles: true,
                  cancelable: true
                });
                selectElement.dispatchEvent(enterKeyEvent);
              }, 50);
            }, 100);
          }
          
          // Also try blur/focus cycle
          selectElement.blur();
          setTimeout(() => {
            selectElement.focus();
            const focusChangeEvent = new Event('change', { 
              bubbles: true, 
              cancelable: true 
            });
            selectElement.dispatchEvent(focusChangeEvent);
          }, 50);
        }
      }, 500);
      
      return true;
    } else {
      // Already set, but force a refresh by triggering change
      const refreshEvent = new Event('change', { 
        bubbles: true, 
        cancelable: true 
      });
      Object.defineProperty(refreshEvent, 'target', {
        value: selectElement,
        enumerable: true,
        configurable: true
      });
      selectElement.dispatchEvent(refreshEvent);
      
      // Also try clicking to refresh
      selectElement.click();
      return true;
    }
  }
  
  return false;
};

/**
 * Helper function to get language from localStorage
 */
const getLanguageFromLocalStorage = (): { code: string; name: string; flag: string } => {
  try {
    const storedLangCode = localStorage.getItem(STORAGE_KEY);
    
    if (storedLangCode) {
      const matchedLang = SUPPORTED_LANGUAGES.find(l => l.code === storedLangCode);
      if (matchedLang) {
        return matchedLang;
      }
    }
  } catch (error) {
    // Error reading language from localStorage
  }
  
  // Default to English
  return SUPPORTED_LANGUAGES[0];
};

/**
 * Text Translation component with dropdown for English, French, and Spanish
 * Uses Google Translate API for on-the-fly translation
 */
export const TextTranslation: React.FC<TextTranslationProps> = ({ 
  className = '',
  showLabel = true 
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<{ code: string; name: string; flag: string }>(() => getLanguageFromLocalStorage());
  const [isReady, setIsReady] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const elementId = 'google_translate_element_text_translation';

  // Set cookie from localStorage BEFORE loading Google Translate script
  // This ensures Google Translate detects the language during initialization
  useEffect(() => {
    setCookieFromLocalStorage();
  }, []);

  // Load Google Translate script
  useEffect(() => {
    // Set cookie again before script loads to ensure it's fresh
    setCookieFromLocalStorage();
    
    if (googleTranslateScriptLoaded) {
      // Check if API is ready
      const checkAPI = setInterval(() => {
        if ((window as any).google?.translate?.TranslateElement) {
          setIsReady(true);
          clearInterval(checkAPI);
        }
      }, 100);
      return () => clearInterval(checkAPI);
    }

    // Check if script already exists
    const existingScript = document.getElementById('google-translate-script');
    if (existingScript) {
      googleTranslateScriptLoaded = true;
      const checkAPI = setInterval(() => {
        if ((window as any).google?.translate?.TranslateElement) {
          setIsReady(true);
          clearInterval(checkAPI);
        }
      }, 100);
      return () => clearInterval(checkAPI);
    }

    // Create unique callback name
    const callbackName = `googleTranslateElementInit_${Date.now()}`;
    
    // Create and load Google Translate script
    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = `https://translate.google.com/translate_a/element.js?cb=${callbackName}`;
    script.async = true;
    
    // Set up callback
    (window as any)[callbackName] = () => {
      googleTranslateScriptLoaded = true;
      // Set cookie again right before initialization
      setCookieFromLocalStorage();
      setTimeout(() => {
        if ((window as any).google?.translate?.TranslateElement) {
          setIsReady(true);
        }
      }, 500);
    };

    document.head.appendChild(script);
    googleTranslateScriptLoaded = true;

    return () => {
      delete (window as any)[callbackName];
    };
  }, []);

  // Initialize Google Translate element
  useEffect(() => {
    if (!isReady) return;

    // Create a hidden container for Google Translate if it doesn't exist
    let container = document.getElementById('google-translate-hidden-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'google-translate-hidden-container';
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.visibility = 'hidden';
      document.body.appendChild(container);
    }

    // Check if element already exists
    let translateDiv = document.getElementById(elementId);
    if (!translateDiv) {
      translateDiv = document.createElement('div');
      translateDiv.id = elementId;
      container.appendChild(translateDiv);
    }

    // Initialize after a short delay
    const initTimer = setTimeout(() => {
      try {
        if (!(window as any).google?.translate?.TranslateElement) {
          return;
        }

        const element = document.getElementById(elementId);
        if (!element) {
          return;
        }

        // Check if already initialized
        if (element.innerHTML.trim() !== '') {
          return;
        }

        // Set cookie one more time right before initialization
        setCookieFromLocalStorage();
        
        // Initialize Google Translate with only our 3 languages
        // Google Translate will automatically detect the cookie and apply translation
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            includedLanguages: 'en,fr,es',
            layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
            multilanguagePage: true,
          },
          elementId
        );

        // Apply language from localStorage after initialization
        // Wait longer to ensure select element is fully populated and translation is applied
        setTimeout(() => {
          applyLanguageFromLocalStorage();
        }, 2000);
      } catch (error) {
        // Error initializing Google Translate
      }
    }, 300);

    return () => {
      clearTimeout(initTimer);
    };
  }, [isReady]);

  // Function to apply language from localStorage (no cookies, direct control)
  const applyLanguageFromLocalStorage = useCallback(() => {
    try {
      // Read from localStorage (source of truth)
      const storedLangCode = localStorage.getItem(STORAGE_KEY);
      
      if (!storedLangCode) {
        return;
      }

      const matchedLang = SUPPORTED_LANGUAGES.find(l => l.code === storedLangCode);
      if (!matchedLang) {
        return;
      }

      // Update UI state from localStorage
      setSelectedLanguage(matchedLang);
      
      // If English is selected, no translation needed
      if (storedLangCode === 'en') {
        // English should be a hard reset state.
        clearAllGoogtransCookies();
        return;
      }
      
      // Apply translation directly via select element (no cookies needed)
      // Wait a bit longer for Google Translate to be fully ready
      setTimeout(() => {
        if (applyTranslationFromLocalStorage(storedLangCode, true)) {
          return;
        }

        // Poll for Google Translate select element if not ready
        let attempts = 0;
        const maxAttempts = 30; // Increased attempts
        const pollInterval = setInterval(() => {
          attempts++;
          
          // Check if select element exists and has options
          const selectElement = document.querySelector('.goog-te-combo') as HTMLSelectElement;
          if (selectElement && selectElement.options && selectElement.options.length > 0) {
            if (applyTranslationFromLocalStorage(storedLangCode, true)) {
              clearInterval(pollInterval);
              return;
            }
          }
          
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            // Last attempt after a longer delay
            setTimeout(() => {
              applyTranslationFromLocalStorage(storedLangCode, true);
            }, 500);
          }
        }, 400); // Increased interval
      }, 1000); // Wait 1 second before trying
    } catch (error) {
      // Error applying language
    }
  }, []);

  // Apply language from localStorage when ready
  useEffect(() => {
    if (isReady) {
      // Wait longer to ensure Google Translate select is fully ready
      const timer = setTimeout(() => {
        applyLanguageFromLocalStorage();
      }, 2000);
      
      // Also try after window load event as fallback
      const handleLoad = () => {
        setTimeout(() => {
          applyLanguageFromLocalStorage();
        }, 1000);
      };
      
      if (document.readyState === 'complete') {
        handleLoad();
      } else {
        window.addEventListener('load', handleLoad);
      }
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('load', handleLoad);
      };
    }
  }, [isReady, applyLanguageFromLocalStorage]);

  // Handle language selection - uses localStorage and syncs to cookie
  const handleLanguageSelect = (language: { code: string; name: string; flag: string }) => {
    const langCode = language.code;
    
    // Save to localStorage (source of truth)
    try {
      localStorage.setItem(STORAGE_KEY, langCode);
      
      // Sync to cookie immediately so Google Translate can use it
      setCookieFromLocalStorage();
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { langCode } }));
    } catch (error) {
      // Error saving to localStorage
    }
    
    // Update UI state
    setSelectedLanguage(language);
    setIsDropdownOpen(false);

    // English is a hard reset path: clear translate state and reload cleanly.
    if (langCode === 'en') {
      clearAllGoogtransCookies();
      // Clear URL hash occasionally used by translation helpers.
      const cleanUrl = `${window.location.pathname}${window.location.search}`;
      window.location.replace(cleanUrl);
      return;
    }
    
    // Try to apply translation directly first
    const applied = applyTranslationFromLocalStorage(langCode);
    
    // If translation was applied, wait a moment then reload to ensure it persists
    if (applied) {
      setTimeout(() => {
        window.location.reload();
      }, 300);
      return;
    }
    
    // If not applied immediately, poll for the select element
    let attempts = 0;
    const maxAttempts = 10;
    
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(() => {
      attempts++;
      if (applyTranslationFromLocalStorage(langCode)) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        // Reload after successful application
        setTimeout(() => {
          window.location.reload();
        }, 300);
        return;
      }
      
      if (attempts >= maxAttempts) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        // Reload page - cookie is already set, so Google Translate will apply translation on reload
        window.location.reload();
      }
    }, 200);
  };

  // Cleanup poll interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <>
      <div ref={dropdownRef} className={`relative ${className}`}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
          title="Translate Website"
          aria-label="Translate website to different languages"
        >
          <Languages className="w-4 h-4 text-gray-500" />
          {showLabel && (
            <span className="text-sm font-medium text-gray-700 hidden md:inline">
              {selectedLanguage.flag} {selectedLanguage.name}
            </span>
          )}
          {!showLabel && (
            <span className="text-sm font-medium text-gray-700">
              {selectedLanguage.flag}
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-lg shadow-xl z-[9999]">
            <div className="p-2">
              {SUPPORTED_LANGUAGES.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageSelect(language)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-blue-50 transition-colors ${
                    selectedLanguage?.code === language.code ? 'bg-blue-100 font-semibold' : ''
                  }`}
                >
                  <span className="text-lg">{language.flag}</span>
                  <span className="text-sm text-gray-700">{language.name}</span>
                  {selectedLanguage?.code === language.code && (
                    <span className="ml-auto text-blue-600">✓</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Google Translate widget styles */}
      <style>{`
        .goog-te-banner-frame {
          display: none !important;
        }
        .skiptranslate {
          display: none !important;
        }
        body {
          top: 0 !important;
        }
        body.top {
          padding-top: 0 !important;
        }
      `}</style>
    </>
  );
};

